
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { type AIModel } from "../types";
import { toast } from "sonner";

export const useOrganizationModels = (organizationId?: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["organizationModels", organizationId],
    queryFn: async () => {
      try {
        // If no organization ID, return empty array immediately
        if (!organizationId) {
          console.log('No organization ID provided, skipping fine-tuned models fetch');
          return [];
        }
        
        console.log('Fetching fine-tuned models for organization:', organizationId);
        
        // Get both succeeded and non-succeeded models to properly track status
        const { data: orgModels, error } = await supabase
          .from('organization_fine_tuned_models')
          .select('model_id, model_name, model_alias, status, created_at')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });
            
        if (error) {
          console.error('Error fetching organization models:', error);
          throw error;
        }
        
        console.log('Found organization fine-tuned models:', orgModels);
        
        if (!orgModels || orgModels.length === 0) {
          return [];
        }
        
        // Format all models to match AIModel interface, using alias if available
        const formattedModels: AIModel[] = orgModels
          .filter(model => model.status === 'succeeded' && model.model_name && model.model_name !== 'pending')
          .map(model => {
            const displayName = model.model_alias || model.model_name;
            const createdDate = new Date(model.created_at).toLocaleDateString();
            
            return {
              value: model.model_name, // Always use the actual model name as the value
              label: `${displayName} (Fine-tuned ${createdDate})`
            };
          });
            
        console.log('Formatted organization fine-tuned models:', formattedModels);
        return formattedModels;
      } catch (error) {
        console.error('Error in useOrganizationModels:', error);
        toast.error("Failed to fetch organization fine-tuned models");
        throw error;
      }
    },
    enabled: !!organizationId, // Only run query if organizationId exists
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep unused data for 10 minutes
  });
};
